import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { formatDateOnly, todayDateOnly, upcomingDays } from '../../core/datetime/ist';
import { OpsService, RosterOfficer } from '../../core/services/ops.service';
import { RbacService } from '../../core/services/rbac.service';

@Component({
  selector: 'app-roster',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './roster.component.html',
})
export class RosterComponent {
  private readonly ops = inject(OpsService);
  private readonly rbac = inject(RbacService);
  readonly roster = this.ops.rosterList;
  readonly selectedId = signal<string | number>('');
  readonly assignment = signal('');
  readonly message = signal<string | null>(null);
  readonly busy = signal(false);
  readonly slotDate = signal(todayDateOnly());
  readonly calendarDays = upcomingDays(7);
  /** Peshkar / admin (roster module). */
  readonly canMutate = computed(() => this.rbac.canModule('roster'));

  readonly selected = computed((): RosterOfficer | undefined => {
    const list = this.roster();
    if (!list.length) return undefined;
    return list.find((o) => o.id === this.selectedId()) ?? list[0];
  });

  constructor() {
    effect(() => {
      const o = this.selected();
      this.assignment.set(o?.currentAssignment || '');
    });
  }

  selectDay(iso: string): void {
    this.slotDate.set(iso);
  }

  calendarActive(iso: string): boolean {
    return this.slotDate() === iso;
  }

  formatLeaveUntil(value?: string | null): string {
    if (!value) return 'On duty';
    return `On leave till ${formatDateOnly(value)}`;
  }

  loadWidth(workload: number): number {
    return Math.min(workload * 8, 100);
  }

  async assign(): Promise<void> {
    if (!this.canMutate()) {
      this.message.set('Your role cannot assign roster.');
      return;
    }
    const o = this.selected();
    if (!o) return;
    this.busy.set(true);
    try {
      this.message.set(
        await this.ops.assignRosterTask({
          officerRef: o.officerRef,
          task: this.assignment(),
          slotDate: this.slotDate(),
        }),
      );
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Assign failed');
    } finally {
      this.busy.set(false);
    }
  }

  async remove(): Promise<void> {
    if (!this.canMutate()) {
      this.message.set('Your role cannot change roster assignments.');
      return;
    }
    const o = this.selected();
    if (!o) return;
    this.busy.set(true);
    try {
      this.message.set(await this.ops.clearRosterTask(o.officerRef));
      this.assignment.set('');
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      this.busy.set(false);
    }
  }

  async update(): Promise<void> {
    if (!this.canMutate()) {
      this.message.set('Your role cannot update availability.');
      return;
    }
    const o = this.selected();
    if (!o) return;
    this.busy.set(true);
    try {
      const next =
        o.availability === 'In Hearing'
          ? 'available'
          : o.availability === 'On Leave'
            ? 'available'
            : 'booked';
      this.message.set(await this.ops.updateRosterStatus(o.id, next));
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Update failed');
    } finally {
      this.busy.set(false);
    }
  }
}
